import * as React from 'react'
import { motion, type Variants } from 'framer-motion'
import { cn } from '../../utils/cn'
import { useTheme } from '../providers/ThemeProvider'

export interface LoadingProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'spinner' | 'dots' | 'pulse' | 'bounce' | 'wave' | 'bars' | 'squares' | 'rings'
  text?: string
  color?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'error'
  speed?: 'slow' | 'normal' | 'fast'
}

const Loading = React.forwardRef<HTMLDivElement, LoadingProps>(
  ({ className, size = 'md', variant = 'spinner', text, color = 'primary', speed = 'normal', ...props }, ref) => {
    const { themeType } = useTheme()
    const isChildVariant = themeType === 'child'

    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
      xl: 'w-12 h-12'
    }

    const textSizes = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    }

    const speedMultiplier = {
      slow: 1.5,
      normal: 1,
      fast: 0.5
    }

    const getColorClasses = () => {
      switch (color) {
        case 'secondary':
          return isChildVariant ? 'text-child-secondary' : 'text-therapist-secondary'
        case 'accent':
          return isChildVariant ? 'text-child-accent' : 'text-therapist-accent'
        case 'success':
          return 'text-green-500'
        case 'warning':
          return 'text-yellow-500'
        case 'error':
          return 'text-red-500'
        default:
          return isChildVariant ? 'text-child-primary' : 'text-therapist-primary'
      }
    }

    const currentSpeed = speedMultiplier[speed]

    const spinnerAnimation: Variants = {
      animate: {
        rotate: 360,
        transition: {
          duration: 1 * currentSpeed,
          repeat: Infinity,
          ease: "linear"
        }
      }
    }

    const dotsAnimation: Variants = {
      animate: {
        scale: [1, 1.2, 1],
        opacity: [0.3, 1, 0.3],
        transition: {
          duration: 1.5 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const pulseAnimation: Variants = {
      animate: {
        scale: [1, 1.1, 1],
        opacity: [0.5, 1, 0.5],
        transition: {
          duration: 2 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const bounceAnimation: Variants = {
      animate: {
        y: [0, -10, 0],
        transition: {
          duration: 0.6 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const waveAnimation: Variants = {
      animate: {
        x: [0, 20, 0],
        transition: {
          duration: 1.2 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const barsAnimation: Variants = {
      animate: {
        scaleY: [1, 0.3, 1],
        transition: {
          duration: 0.8 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const squaresAnimation: Variants = {
      animate: {
        rotate: [0, 90, 180, 270, 360],
        scale: [1, 0.8, 1],
        transition: {
          duration: 1.5 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const ringsAnimation: Variants = {
      animate: {
        scale: [1, 1.2, 1],
        opacity: [1, 0.3, 1],
        transition: {
          duration: 1.8 * currentSpeed,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    }

    const renderSpinner = () => (
      <motion.div
        className={cn(
          'border-2 border-gray-200 border-t-current rounded-full',
          sizeClasses[size],
          getColorClasses()
        )}
        variants={spinnerAnimation}
        animate="animate"
      />
    )

    const renderDots = () => (
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'rounded-full',
              size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-6 h-6',
              getColorClasses()
            )}
            variants={dotsAnimation}
            animate="animate"
            transition={{ delay: i * 0.15 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderPulse = () => (
      <motion.div
        className={cn(
          'rounded-full',
          sizeClasses[size],
          getColorClasses()
        )}
        variants={pulseAnimation}
        animate="animate"
      />
    )

    const renderBounce = () => (
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'rounded-full',
              size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-6 h-6',
              getColorClasses()
            )}
            variants={bounceAnimation}
            animate="animate"
            transition={{ delay: i * 0.1 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderWave = () => (
      <div className="flex space-x-1">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'rounded-full',
              size === 'sm' ? 'w-1 h-4' : size === 'md' ? 'w-1.5 h-6' : size === 'lg' ? 'w-2 h-8' : 'w-3 h-12',
              getColorClasses()
            )}
            variants={waveAnimation}
            animate="animate"
            transition={{ delay: i * 0.1 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderBars = () => (
      <div className="flex space-x-1 items-end">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'rounded-sm',
              size === 'sm' ? 'w-1 h-3' : size === 'md' ? 'w-1.5 h-4' : size === 'lg' ? 'w-2 h-6' : 'w-3 h-8',
              getColorClasses()
            )}
            variants={barsAnimation}
            animate="animate"
            transition={{ delay: i * 0.1 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderSquares = () => (
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'rounded-sm',
              size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-6 h-6',
              getColorClasses()
            )}
            variants={squaresAnimation}
            animate="animate"
            transition={{ delay: i * 0.2 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderRings = () => (
      <div className="relative">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={cn(
              'absolute border-2 rounded-full',
              size === 'sm' ? 'w-4 h-4' : size === 'md' ? 'w-6 h-6' : size === 'lg' ? 'w-8 h-8' : 'w-12 h-12',
              getColorClasses(),
              'border-current'
            )}
            style={{
              width: size === 'sm' ? 16 + i * 8 : size === 'md' ? 24 + i * 12 : size === 'lg' ? 32 + i * 16 : 48 + i * 24,
              height: size === 'sm' ? 16 + i * 8 : size === 'md' ? 24 + i * 12 : size === 'lg' ? 32 + i * 16 : 48 + i * 24,
            }}
            variants={ringsAnimation}
            animate="animate"
            transition={{ delay: i * 0.3 * currentSpeed }}
          />
        ))}
      </div>
    )

    const renderVariant = () => {
      switch (variant) {
        case 'dots':
          return renderDots()
        case 'pulse':
          return renderPulse()
        case 'bounce':
          return renderBounce()
        case 'wave':
          return renderWave()
        case 'bars':
          return renderBars()
        case 'squares':
          return renderSquares()
        case 'rings':
          return renderRings()
        default:
          return renderSpinner()
      }
    }

    return (
      <motion.div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center space-y-2',
          className
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        {...(props as any)}
      >
        {renderVariant()}
        {text && (
          <motion.p
            className={cn(
              'text-center',
              textSizes[size],
              isChildVariant ? 'text-child-text font-child' : 'text-therapist-textMuted'
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {text}
          </motion.p>
        )}
      </motion.div>
    )
  }
)

Loading.displayName = 'Loading'

export { Loading }